import { useState, useEffect, ReactNode, createContext, useContext } from 'react'
import { Formik } from 'formik'
import { t } from 'i18next'
import { useMutation } from 'react-query'
import { AlertType } from 'src/components/shared/ui/AlertDialog/enums'
import SubmitStagesDialog from 'src/components/shared/ui/SubmitStagesDialog/SubmitStagesDialog'
import {
  SubmitStageProp,
  SubmitStageStatus,
  SubmitStageStep
} from 'src/components/shared/ui/SubmitStagesDialog/SubmitStagesTypes'
import {
  excesoCabidaFileName,
  excesoCabidaSubTipoObject
} from 'src/components/views/EscritosLexnet/components/shared/components/Adjuntos/components/const'
import useEcritoLegalValidation from 'src/components/views/EscritosLexnet/components/shared/hooks/useEscritoLegalValidations'
import useFormikEscrito from 'src/components/views/EscritosLexnet/components/shared/hooks/useFormikEscrito'
import { EscritosFormValues } from 'src/components/views/EscritosLexnet/components/shared/types/escritosFormTypes'
import { LEXNET_MAX_MAIN_FILE_SIZE } from 'src/constants/lexnet'
import { useAlertContext } from 'src/contexts/AlertContext'
import { useEscritoContext } from 'src/contexts/EscritoContext/EscritoContext'
import { useLexnetStatusContext } from 'src/contexts/LexnetStatusContext'
import { useTipoMensajeContext } from 'src/contexts/TipoMensajeContext'
import { TipoEscrito } from 'src/enums/EscritosLegales'
import { FormField } from 'src/enums/Form'
import useBlob from 'src/hooks/useBlob'
import { Escrito } from 'src/interfaces/EscritosLegales'
import { signPdf, signString, startTransaction, endTransaction } from 'src/services/CryptoService'
import {
  EscritoAdjuntosPost,
  getMensajeEscritoCades,
  logError,
  sendEscrito,
  sendEscritoBloque
} from 'src/services/EscritosServices'
import HttpError from 'src/utils/Http/HttpError'
import { getLexnetErrorMessage } from 'src/utils/lexnetErrors'
import { getBlobFromBase64 } from 'src/utils/blob'

type EscritoMutationParams = {
  escrito: Escrito
  values
}

type EscritosFormPropsType = {
  children: ReactNode
}

const EscritosFormContext = createContext({})

export function useEscritosFormContext(): any {
  return useContext(EscritosFormContext)
}

function EscritosForm({ children }: EscritosFormPropsType): JSX.Element {
  const { addAlert } = useAlertContext()
  const [openSubmitDialog, setOpenSubmitDialog] = useState(false)
  const { escrito: escritoContext, initialValues, setEscrito, setSend } = useEscritoContext()
  const { tipoMensaje, isMonitorio } = useTipoMensajeContext()
  const {
    connected,
    showConnectDialog,
    setNewNotificationsOpen,
    setConnectionSuccessOpen,
    setNoConnectionOpen
  } = useLexnetStatusContext()
  const [submitDialogDisabledButton, setSubmitDialogDisabledButton] = useState(true)
  const [selectedSubmitStage, setSelectedSubmitStage] = useState<SubmitStageProp>({
    step: null,
    status: null
  })
  const [validateAfterSubmit, setValidateAfterSubmit] = useState(false)
  const sendTrace: any = []
  let step = 0

  const { getEscrito } = useFormikEscrito()
  const { toBase64 } = useBlob()

  function resetSubmitStages() {
    Object.keys(SubmitStageStep).forEach(key => {
      if (Number.isNaN(Number(SubmitStageStep[key]))) {
        setSelectedSubmitStage({
          step: Number(key),
          status: SubmitStageStatus.INITIAL
        })
      }
    })
  }

  const showMessage = (status: SubmitStageStatus = SubmitStageStatus.PROCESSING, error = '') => {
    setSelectedSubmitStage({
      step,
      status,
      error
    })
  }

  const showNextMessage = () => {
    showMessage(SubmitStageStatus.SUCCESS)
    step += 1
    showMessage()
  }

  const showMessageError = (e: HttpError | TypeError) => {
    let message = t('general.error_lexnet_not_accessible')

    if (e instanceof HttpError) {
      message = getLexnetErrorMessage(e.response, t) || message
    }

    showMessage(SubmitStageStatus.ERROR, message)
  }

  const showSubmitDialog = () => {
    resetSubmitStages()
    setOpenSubmitDialog(true)
  }

  async function getFiles(values: any[], mainFileSign: string): Promise<EscritoAdjuntosPost[]> {
    try {
      sendTrace.push({ initGetFiles: true })
      const mainFile = values[FormField.DOCUMENTO_PRINCIPAL_ARCHIVO].file
      sendTrace.push({ mainFileName: mainFile.name, mainFileSize: mainFile.size })

      const extraFiles = values[FormField.DOCUMENTOS_ANEXOS_ARCHIVO]
      sendTrace.push({ extraFiles: true })

      const esCorreccion = values[FormField.ES_CORRECCION]
      sendTrace.push({ esCorreccion })

      if (!esCorreccion && !mainFile) {
        throw new Error('No se ha indicado Adjunto principal')
      }

      const promises: Promise<string>[] = []
      const files: EscritoAdjuntosPost[] = []

      promises.push(toBase64(mainFile))

      const mainFilePost: EscritoAdjuntosPost = {
        base64: '',
        fileName: mainFile.name,
        fileSize: mainFile.size,
        firma: mainFileSign
      }
      sendTrace.push({
        mainFilePostName: mainFilePost.fileName,
        mainFilePostSize: mainFilePost.fileSize
      })

      files.push(mainFilePost)
      sendTrace.push({ files: true })

      if (extraFiles && extraFiles.length) {
        extraFiles.forEach(anexo => {
          promises.push(toBase64(anexo.file))
          files.push({
            base64: '',
            fileName: anexo.file.name,
            fileSize: anexo.file.size,
            firma: ''
          })
        })
        sendTrace.push({ extraFiles: true })
      }

      await Promise.all(promises).then(base64 => {
        base64.forEach((fileBase64: string, index: number) => {
          files[index].base64 = fileBase64
        })
      })

      sendTrace.push({ endGetFiles: true })
      return files
    } catch (e) {
      logError({
        origen: 'getFiles',
        error: e,
        traza: (e as any).stack,
        trazaDev: JSON.stringify(sendTrace)
      })
      throw new Error(e as any)
    }
  }

  async function getAnexoFiles(
    files: EscritoAdjuntosPost[],
    values: any[]
  ): Promise<EscritoAdjuntosPost[]> {
    try {
      sendTrace.push({ initGetFiles: true })
      const extraFiles = values[FormField.DOCUMENTOS_ANEXOS_ARCHIVO]
      sendTrace.push({ extraFiles: true })

      const promises: Promise<string>[] = []
      if (extraFiles && extraFiles.length) {
        extraFiles.forEach(anexo => {
          promises.push(toBase64(anexo.file))
          files.push({
            base64: '',
            fileName: anexo.file.name,
            fileSize: anexo.file.size,
            firma: ''
          })
        })
        sendTrace.push({ extraFiles: true })
      }
      await Promise.all(promises).then(base64 => {
        base64.forEach((fileBase64: string, index: number) => {
          files[index + 1].base64 = fileBase64
        })
      })

      sendTrace.push({ endGetFiles: true })
      return files
    } catch (e) {
      logError({
        origen: 'getFiles',
        error: e,
        traza: (e as any).stack,
        trazaDev: JSON.stringify(sendTrace)
      })
      throw new Error(e as any)
    }
  }

  const setExcesoCabidaFile = (
    escrito: Escrito,
    values: any[],
    excesoCabidaFile: EscritoAdjuntosPost
  ) => {
    const justificanteFile = { file: getBlobFromBase64(excesoCabidaFile) }
    delete escrito.documentosAnexos
    escrito.excesoCabida = false
    escrito.documentoPrincipal[0].pendienteCompletar = true
    values[FormField.DOCUMENTOS_ANEXOS_ARCHIVO] = []
    if (values[FormField.DOCUMENTO_PRINCIPAL_ARCHIVO].file.size > LEXNET_MAX_MAIN_FILE_SIZE) {
      values[FormField.DOCUMENTO_PRINCIPAL_ARCHIVO] = justificanteFile
      escrito.documentoPrincipal[0].archivo = excesoCabidaFile.fileName
      escrito.documentoPrincipal[0].hash = ''
      escrito.documentoPrincipal[0].sign = true
      escrito.documentoPrincipal[0].subtipo = excesoCabidaSubTipoObject
    } else {
      values[FormField.DOCUMENTOS_ANEXOS_ARCHIVO].push(justificanteFile)
      escrito.documentosAnexos = []
      const anexoFile = {
        archivo: excesoCabidaFile.fileName,
        hash: '',
        subtipo: excesoCabidaSubTipoObject
      }
      escrito.documentosAnexos.push(anexoFile)
    }
  }

  const sendEscritoExcesoCabidaMutation = useMutation(
    async ({ escrito, values }: EscritoMutationParams) => {
      setSubmitDialogDisabledButton(true)
      showSubmitDialog()
      showNextMessage()

      // ***PRIMERA LLAMADA
      const resExcesoCabida = await sendEscrito(escrito)
      const excesoCabidaBase64 = resExcesoCabida.bytesMIME
      const excesoCabidaFile: EscritoAdjuntosPost[] = [
        {
          base64: excesoCabidaBase64,
          fileName: `${excesoCabidaFileName}_${Date.now()}.pdf`,
          fileSize: excesoCabidaBase64.length,
          firma: ''
        }
      ]
      setExcesoCabidaFile(escrito, values, excesoCabidaFile[0])

      const res = await sendEscrito(escrito)

      showNextMessage()
      startTransaction()
      showNextMessage()
      const signedToken = await signString(res.token)

      const mainFile = values[FormField.DOCUMENTO_PRINCIPAL_ARCHIVO].file
      const mainFileBase64 = await toBase64(mainFile)
      let signedPdf = mainFileBase64
      if (escrito.documentoPrincipal[0].sign) {
        signedPdf = await signPdf(mainFileBase64)
      }
      showNextMessage()
      // ***SEGUNDA LLAMADA
      const files: EscritoAdjuntosPost[] = await getFiles(values, signedPdf)

      const mensajeCades = await getMensajeEscritoCades(res.idEnvio, signedToken, files, false)
      const signedCadesMessage = await signString(mensajeCades.mensajeBase64, true)
      endTransaction()
      // ***TERCERA LLAMADA
      showNextMessage()
      const { escrito: escritoEnviado } = await sendEscritoBloque(
        res.idEnvio,
        res.idEscrito,
        signedToken,
        signedCadesMessage,
        files,
        res.tamBloque
      )
      escrito.idAcuseLexnet = (escritoEnviado as any).idAcuse
      setEscrito(escrito)
    },
    {
      onSuccess: () => {
        showMessage(SubmitStageStatus.SUCCESS)
      },
      onError: (e: HttpError) => {
        logError({
          origen: 'sendEscritoExcesoCabidaMutation',
          error: e,
          traza: e.stack,
          trazaDev: ''
        })
        showMessageError(e)
      },
      onSettled: () => {
        setSubmitDialogDisabledButton(false)
      }
    }
  )

  const sendEscritoSubsanacionMutation = useMutation(
    async ({ escrito }: EscritoMutationParams) => {
      setSubmitDialogDisabledButton(true)
      showSubmitDialog()
      showNextMessage()

      // ***PRIMERA LLAMADA
      const res = await sendEscrito(escrito)
      showNextMessage()
      startTransaction()
      showNextMessage()
      const signedToken = await signString(res.token)
      showNextMessage()

      // ***SEGUNDA LLAMADA
      const files: EscritoAdjuntosPost[] = []

      let mensajeCades = await getMensajeEscritoCades(res.idEnvio, signedToken, files, true)

      const signedFixedPdf = await signPdf(mensajeCades.correccionPdf.base64)

      files.push({
        base64: signedFixedPdf,
        fileSize: signedFixedPdf.length,
        fileName: mensajeCades.correccionPdf.fileName,
        firma: signedFixedPdf
      })

      mensajeCades = await getMensajeEscritoCades(res.idEnvio, signedToken, files, false)

      const signedCadesMessage = await signString(mensajeCades.mensajeBase64, true)
      endTransaction()

      // ***TERCERA LLAMADA
      showNextMessage()
      const { escrito: escritoEnviado } = await sendEscritoBloque(
        res.idEnvio,
        res.idEscrito,
        signedToken,
        signedCadesMessage,
        files,
        res.tamBloque
      )
      escrito.idAcuseLexnet = (escritoEnviado as any).idAcuse
      setEscrito(escrito)
    },
    {
      onSuccess: () => {
        showMessage(SubmitStageStatus.SUCCESS)
      },
      onError: (e: HttpError) => {
        logError({
          origen: 'sendEscritoSubsanacionMutation',
          error: e,
          traza: e.stack,
          trazaDev: ''
        })
        showMessageError(e)
      },
      onSettled: () => {
        setSubmitDialogDisabledButton(false)
      }
    }
  )

  const sendEscritoMonitorioMutation = useMutation(
    async ({ escrito, values }: EscritoMutationParams) => {
      setSubmitDialogDisabledButton(true)
      showSubmitDialog()
      showNextMessage()

      // ***PRIMERA LLAMADA
      const res = await sendEscrito(escrito)
      showNextMessage()
      startTransaction()
      showNextMessage()
      const signedToken = ''
      showNextMessage()

      // ***SEGUNDA LLAMADA
      const files: EscritoAdjuntosPost[] = []

      let mensajeCades = await getMensajeEscritoCades(res.idEnvio, signedToken, files, true)

      const signedFixedPdf = await signPdf(mensajeCades.correccionPdf.base64)

      files.push({
        base64: signedFixedPdf,
        fileSize: signedFixedPdf.length,
        fileName: mensajeCades.correccionPdf.fileName,
        firma: signedFixedPdf
      })

      const filesAll: EscritoAdjuntosPost[] = await getAnexoFiles(files, values)

      mensajeCades = await getMensajeEscritoCades(res.idEnvio, signedToken, filesAll, false)

      const signedCadesMessage = await signString(mensajeCades.mensajeBase64, true)
      endTransaction()

      // ***TERCERA LLAMADA
      showNextMessage()
      const { escrito: escritoEnviado } = await sendEscritoBloque(
        res.idEnvio,
        res.idEscrito,
        signedToken,
        signedCadesMessage,
        files,
        res.tamBloque
      )
      escrito.idAcuseLexnet = (escritoEnviado as any).idAcuse
      setEscrito(escrito)
    },
    {
      onSuccess: () => {
        showMessage(SubmitStageStatus.SUCCESS)
      },
      onError: (e: HttpError) => {
        logError({
          origen: 'sendEscritoMonitorioMutation',
          error: e,
          traza: e.stack,
          trazaDev: ''
        })
        showMessageError(e)
      },
      onSettled: () => {
        setSubmitDialogDisabledButton(false)
      }
    }
  )

  const sendEscritoMutation = useMutation(
    async ({ escrito, values }: EscritoMutationParams) => {
      closeLexnetConexionModals()
      const mainFile = values[FormField.DOCUMENTO_PRINCIPAL_ARCHIVO].file
      sendTrace.push({ mainFileName: mainFile.name, mainFileSize: mainFile.size })
      if (!mainFile) {
        throw new Error('No se ha indicado Adjunto principal')
      }
      const mainFileBase64 = await toBase64(mainFile)
      sendTrace.push({ mainFileBase64: true })
      setSubmitDialogDisabledButton(true)
      showSubmitDialog()
      showNextMessage() // start step 1
      sendTrace.push({ initStep1: true })
      // ***PRIMERA LLAMADA
      const res = await sendEscrito(escrito)
      sendTrace.push({ res: true })
      if (!res) {
        throw new Error('Error enviando el escrito, por favor, inténtelo de nuevo mas tarde')
      }
      showNextMessage() // end step 1 => start step 2
      sendTrace.push({ initStep2: true })
      startTransaction()
      showNextMessage() // end step 2 => start step 3
      sendTrace.push({ initStep3: true })
      const signedToken = await signString(res.token)
      sendTrace.push({ signedToken: true })
      if (!signedToken) {
        throw new Error(
          'Error obteniendo la firma desde autofirma, por favor, inténtelo de nuevo mas tarde'
        )
      }
      let signedPdf = mainFileBase64
      if (escrito.documentoPrincipal[0].sign) {
        signedPdf = await signPdf(mainFileBase64)
        sendTrace.push({ signedPdf: true })
        if (!signedPdf) {
          throw new Error('Error firmando el documento, por favor, inténtelo de nuevo mas tarde')
        }
      }
      showNextMessage() // end step 3 => start step 4
      sendTrace.push({ initStep4: true })
      // ***SEGUNDA LLAMADA
      const files: EscritoAdjuntosPost[] = await getFiles(values, signedPdf)
      sendTrace.push({ files: true })
      const mensajeCades = await getMensajeEscritoCades(res.idEnvio, signedToken, files, false)
      sendTrace.push({ mensajeCades: true })
      const signedCadesMessage = await signString(mensajeCades.mensajeBase64, true)
      sendTrace.push({ signedCadesMessage: true })
      endTransaction()
      // ***TERCERA LLAMADA
      showNextMessage() // end step 4 => start step 5
      sendTrace.push({ initStep5: true })
      const { escrito: escritoEnviado } = await sendEscritoBloque(
        res.idEnvio,
        res.idEscrito,
        signedToken,
        signedCadesMessage,
        files,
        res.tamBloque
      )
      sendTrace.push({ initStep51: true })
      escrito.idAcuseLexnet = (escritoEnviado as any).idAcuse
      sendTrace.push({ escrito: true })
      setEscrito(escrito)
      showNextMessage() // end step 5connected
    },
    {
      onSuccess: async () => {
        showMessage(SubmitStageStatus.SUCCESS)
      },
      onError: (e: HttpError) => {
        logError({
          origen: 'sendEscritoMutation',
          error: e,
          traza: e.stack,
          trazaDev: JSON.stringify(sendTrace)
        })
        showMessageError(e)
      },
      onSettled: () => {
        setSubmitDialogDisabledButton(false)
      }
    }
  )

  const sendWithExesoDeCabida = async (escrito: Escrito, values: EscritosFormValues) => {
    sendEscritoExcesoCabidaMutation.mutate({ escrito, values })
  }

  async function handleSubmit(values: EscritosFormValues) {
    if (!connected) {
      showConnectDialog(true)
      return false
    }

    const excesoCabida = values[FormField.EXCESO_CABIDA]
    // TODO: Añadir 'esCorreccion' a Formik
    const esCorreccion = values[FormField.ES_CORRECCION]

    const escrito: Escrito = getEscrito(values)

    escrito.tipo = { id: TipoEscrito.ESCRITO, descripcion: 'Escrito' }
    escrito.tipoEscrito = { id: 0, codigo: tipoMensaje, descripcion: '' }

    if (escritoContext) {
      escrito.tipo = escritoContext.tipo
      escrito.tipoEscrito = escritoContext.tipoEscrito
    }
    if (!excesoCabida && !esCorreccion && !isMonitorio) {
      // Envío de Escrito por defecto
      sendEscritoMutation.mutate({ escrito, values })
    } else if (isMonitorio) {
      // Envío de Monitorio
      sendEscritoMonitorioMutation.mutate({ escrito, values })
    } else if (esCorreccion) {
      // Envío de Escrito de Subsanación
      sendEscritoSubsanacionMutation.mutate({ escrito, values })
    } else {
      // Envío de Escrito con Exceso de cabida
      const FileSizeMaxError = t('form.validation.filename_max_error_modal')

      addAlert({
        message: FileSizeMaxError,
        type: AlertType.INFO,
        action: () => {
          sendWithExesoDeCabida(escrito, values)
        }
      })
    }
    return true
  }

  useEffect(() => {
    if (escritoContext?.idAcuseLexnet > 0) {
      setSend(true)
    }
  }, [escritoContext])

  function closeSubmitDialog() {
    setOpenSubmitDialog(false)
  }

  function closeLexnetConexionModals() {
    if (connected) {
      showConnectDialog(false)
      setNewNotificationsOpen(false)
      setConnectionSuccessOpen(false)
      setNoConnectionOpen(false)
    }
  }

  return (
    <>
      <Formik
        initialValues={initialValues}
        validationSchema={useEcritoLegalValidation(initialValues[FormField.ES_CORRECCION])}
        onSubmit={handleSubmit}
        validateOnMount={false}
        validateOnChange={validateAfterSubmit}
        validateOnBlur={false}
        enableReinitialize
      >
        <EscritosFormContext.Provider value={{ validateAfterSubmit, setValidateAfterSubmit }}>
          {children}
        </EscritosFormContext.Provider>
      </Formik>
      <SubmitStagesDialog
        show={openSubmitDialog}
        selectedStage={selectedSubmitStage}
        close={() => closeSubmitDialog()}
        disabledButton={submitDialogDisabledButton}
      />
    </>
  )
}

export default EscritosForm
